<template>
    <div class="chatbot-agent" v-animate-css="{ classes: 'slideInUp', delay: 0, duration: 300 }">
        <section class="header">
            <span class="d-flex align-items-center gap-5">
                <img src="/static/img/copilot.png" width="32" height="32" />
                <div class="d-flex flex-column">
                    <strong class="font-weight-bold" style="font-size: 18px; letter-spacing: 1px">Supsis Asistan</strong>
                    <small :class="connectionStatus === 'connected' ? 'text-success' : 'text-warning'">
                        <a-badge :color="connectionStatus === 'connected' ? 'green' : 'orange'" />
                        {{ connectionStatus === 'connected' ? 'Chatbot Tasarım Uzmanı' : 'Bağlanıyor...' }}
                    </small>
                </div>
            </span>
            <div>
                <md-button class="md-icon-button" @click="handleClose">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
        </section>
        
        <section class="chat-conversation">
            <div class="conversation">
                <!-- Chat Messages -->
                <div class="chat-messages" ref="messagesContainer">
                    <div v-if="!messages.length" class="welcome-message">
                        <div class="welcome-content">
                            <img src="/static/img/copilot.png" width="48" height="48" />
                            <h4>Merhaba! Ben Supsis Chatbot Asistanınızım 👋</h4>
                            <p>Chatbot tasarımınızla ilgili size nasıl yardım edebilirim?</p>
                            <div class="quick-actions">
                                <div class="quick-action" @click="sendQuickMessage('Yeni bir karşılama botu yapmak istiyorum')">
                                    <i class="material-icons">add_circle</i>
                                    <span>Yeni bot oluştur</span>
                                </div>
                                <div class="quick-action" @click="sendQuickMessage('Mevcut botumu optimize etmek istiyorum')">
                                    <i class="material-icons">tune</i>
                                    <span>Bot'u optimize et</span>
                                </div>
                                <div class="quick-action" @click="sendQuickMessage('Botuma AI özelliği eklemek istiyorum')">
                                    <i class="material-icons">psychology</i>
                                    <span>AI özelliği ekle</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Message List -->
                    <div v-for="message in messages" :key="message.id" :class="['message', message.type]">
                        <!-- User Message -->
                        <div v-if="message.type === 'user'" class="user-message">
                            <div class="message-content">
                                {{ message.content }}
                            </div>
                            <div class="message-time">{{ formatTime(message.timestamp) }}</div>
                        </div>

                        <!-- Agent Message -->
                        <div v-else-if="message.type === 'agent'" class="agent-message">
                            <div class="agent-avatar">
                                <img src="/static/img/copilot.png" width="32" height="32" />
                            </div>
                            <div class="message-bubble modern">
                                <div class="message-content" v-html="formatMessage(message.content)"></div>
                                <div class="message-time">{{ formatTime(message.timestamp) }}</div>
                            </div>
                        </div>

                        <!-- System Message -->
                        <div v-else-if="message.type === 'system'" class="system-message">
                            <div class="system-content">
                                <i class="material-icons">info</i>
                                <span>{{ message.content }}</span>
                            </div>
                            <div class="message-time">{{ formatTime(message.timestamp) }}</div>
                        </div>

                        <!-- Function Calls Message -->
                        <div v-else-if="message.type === 'function'" class="function-message">
                            <div class="agent-avatar">
                                <img src="/static/img/copilot.png" width="32" height="32" />
                            </div>
                            <div class="function-bubble">
                                <div class="function-header">
                                    <i class="material-icons">build</i>
                                    <span>İşlemler yapılıyor...</span>
                                </div>
                                <div class="function-list">
                                    <div 
                                        v-for="func in message.functions" 
                                        :key="func.id" 
                                        class="function-item"
                                        :class="func.status"
                                    >
                                        <a-icon 
                                            v-if="func.status === 'pending'" 
                                            type="loading" 
                                            style="color: #1890ff" 
                                        />
                                        <a-icon 
                                            v-else-if="func.status === 'completed'" 
                                            type="check-circle" 
                                            theme="filled" 
                                            style="color: #52c41a" 
                                        />
                                        <a-icon 
                                            v-else-if="func.status === 'failed'" 
                                            type="close-circle" 
                                            theme="filled" 
                                            style="color: #f5222d" 
                                        />
                                        <div class="function-content">
                                            <span class="function-name">{{ getFunctionDisplayName(func.name) }}</span>
                                            <span v-if="func.summary && func.status === 'completed'" class="function-summary">{{ func.summary }}</span>
                                            <span v-if="func.error" class="function-error">{{ func.error }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Loading indicator -->
                    <div v-if="isLoading" class="loading-message">
                        <div class="agent-avatar">
                            <img src="/static/img/copilot.png" width="32" height="32" />
                        </div>
                        <div class="loading-bubble">
                            <div class="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        
        <section class="footer">
            <div class="chat-input">
                <div class="input-container">
                    <a-textarea
                        v-model="inputMessage"
                        :rows="1"
                        :auto-size="{ minRows: 1, maxRows: 3 }"
                        placeholder="Chatbot tasarımınız için ne yapmak istiyorsunuz?"
                        @keydown.enter="handleEnterKey"
                        :disabled="isLoading"
                        ref="messageInput"
                    />
                    <a-button 
                        type="primary" 
                        :disabled="!inputMessage.trim() || isLoading"
                        @click="sendMessage"
                        class="send-button"
                    >
                        <a-icon type="send" />
                    </a-button>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { CHATBOT_API } from "../../../../../../../api/chatbotv2";
import { getToken } from "../../../../../../../utils/auth";

export default {
    name: "ChatbotAgent",
    props: {
        storyId: {
            type: String,
            required: true,
        },
        version: {
            type: String,
            required: true,
        },
        bar: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            sessionId: null,
            messages: [],
            inputMessage: "",
            isLoading: false,
            agentBusy: false,
            sseConnection: null,
            connectionStatus: 'disconnected', // disconnected, connecting, connected
        };
    },
    mounted() {
        // Session ID oluştur
        this.sessionId = this.generateUUID();
        
        // SSE bağlantısını kur
        this.connectSSE();
    },
    beforeDestroy() {
        // SSE bağlantısını kapat
        this.disconnectSSE();
    },
    methods: {
        async sendMessage() {
            if (!this.inputMessage.trim() || this.isLoading || this.agentBusy) return;

            const message = this.inputMessage.trim();
            this.inputMessage = "";

            await this.sendToAgent(message);
        },

        async sendQuickMessage(message) {
            if (this.agentBusy) return;
            await this.sendToAgent(message);
        },

        async sendToAgent(message) {
            if (this.agentBusy) {
                this.addMessage({
                    type: "system",
                    content: "Agent şu anda meşgul, lütfen bekleyin...",
                });
                return;
            }

            this.isLoading = true;

            try {
                console.log('Mesaj gönderiliyor:', {
                    sessionId: this.sessionId,
                    message: message,
                    storyId: this.storyId,
                    version: this.version,
                });

                // Mesajı backend'e gönder (Redis cluster üzerinden)
                const response = await CHATBOT_API.agentSendMessage({
                    sessionId: this.sessionId,
                    message: message,
                    storyId: this.storyId,
                    version: this.version,
                });

                console.log('Mesaj gönderildi, yanıt:', response);

            } catch (error) {
                console.error("Agent mesaj gönderme hatası:", error);
                this.addMessage({
                    type: "agent",
                    content: "Üzgünüm, mesaj gönderilemedi. Lütfen tekrar deneyin.",
                });
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * SSE bağlantısını kur
         */
        connectSSE() {
            try {
                this.connectionStatus = 'connecting';
                
                // Token ve site-id bilgilerini al
                const token = getToken();
                const siteId = this.$store.state.site.site._id;
                
                if (!token || !siteId) {
                    console.error('SSE için token veya site-id bulunamadı');
                    return;
                }
                
                // Headers ile birlikte URL oluştur
                const baseUrl = CHATBOT_API.agentStreamUrl(this.sessionId, this.storyId, this.version);
                const urlWithAuth = `${baseUrl}&token=${encodeURIComponent(token)}&siteId=${encodeURIComponent(siteId)}`;
                
                this.sseConnection = new EventSource(urlWithAuth);

                this.sseConnection.onopen = () => {
                    this.connectionStatus = 'connected';
                    console.log('SSE bağlantısı kuruldu');
                };

                this.sseConnection.onmessage = (event) => {
                    try {
                        console.log('SSE raw event:', event);
                        const data = JSON.parse(event.data);
                        console.log('SSE parsed data:', data);
                        this.handleSSEMessage(data);
                    } catch (error) {
                        console.error('SSE mesaj parse hatası:', error, 'Raw event:', event);
                    }
                };

                this.sseConnection.onerror = (error) => {
                    console.error('SSE bağlantı hatası:', error);
                    this.connectionStatus = 'disconnected';
                    
                    // 3 saniye sonra yeniden bağlan
                    setTimeout(() => {
                        if (this.connectionStatus === 'disconnected') {
                            this.connectSSE();
                        }
                    }, 3000);
                };

            } catch (error) {
                console.error('SSE kurulum hatası:', error);
                this.connectionStatus = 'disconnected';
            }
        },

        /**
         * SSE bağlantısını kapat
         */
        disconnectSSE() {
            if (this.sseConnection) {
                this.sseConnection.close();
                this.sseConnection = null;
            }
            this.connectionStatus = 'disconnected';
        },

        /**
         * SSE mesajlarını handle et
         */
        handleSSEMessage(data) {
            console.log('SSE mesajı alındı:', data);

            // Ping mesajlarını ignore et
            if (data.ping) {
                return;
            }

            switch (data.type) {
                case 'session_ready':
                    this.connectionStatus = 'connected';
                    // Session ready mesajını göster
                    if (data.data && data.data.status === 'connected') {
                        this.addMessage({
                            type: "system",
                            content: "✅ Bağlantı başarıyla kuruldu! Size nasıl yardım edebilirim?",
                        });
                    }
                    break;

                case 'user_message':
                    this.addMessage({
                        type: "user",
                        content: data.data.message,
                        timestamp: data.data.timestamp,
                    });
                    break;

                case 'agent_thinking':
                    this.isLoading = true;
                    this.addMessage({
                        type: "system",
                        content: "🤖 Yapay zeka düşünüyor...",
                    });
                    break;

                case 'agent_message':
                    this.isLoading = false;
                    this.addMessage(data.data);
                    break;

                case 'function_will_start':
                    this.addMessage({
                        type: "system",
                        content: `🚀 ${data.data.message}`,
                    });
                    break;

                case 'function_call_started':
                    this.addMessage({
                        type: "function",
                        functions: [{
                            ...data.data.functionCall,
                            displayName: data.data.displayName,
                            status: "pending"
                        }],
                    });
                    break;

                case 'function_executing':
                    // Function status'unu güncelle
                    this.updateFunctionStatus(data.data.functionId, "executing");
                    
                    // Kullanıcı dostu mesaj ekle
                    if (data.data.message) {
                        this.addMessage({
                            type: "system",
                            content: `⚙️ ${data.data.message}`,
                        });
                    }
                    break;

                case 'function_completed':
                    this.updateFunctionStatus(data.data.functionId, "completed", data.data.summary);
                    this.emitUIUpdates([{ success: true }]);
                    
                    // Kullanıcı dostu tamamlanma mesajı
                    if (data.data.message) {
                        this.addMessage({
                            type: "system",
                            content: data.data.message,
                        });
                    }
                    break;

                case 'function_failed':
                    this.updateFunctionStatus(data.data.functionId, "failed", null, data.data.error);
                    
                    // Kullanıcı dostu hata mesajı
                    if (data.data.message) {
                        this.addMessage({
                            type: "system",
                            content: data.data.message,
                        });
                    }
                    break;

                case 'agent_busy':
                    this.agentBusy = true;
                    this.addMessage({
                        type: "system",
                        content: data.data.message,
                    });
                    break;

                case 'session_state_updated':
                    if (data.data.state && data.data.state.busy !== undefined) {
                        this.agentBusy = data.data.state.busy;
                        this.isLoading = data.data.state.busy;
                    }
                    try {
                        const pretty = JSON.stringify(data.data.state || {}, null, 2);
                        const content = `<strong>Session state güncellendi</strong><br><pre><code>${pretty}</code></pre>`;
                        this.addMessage({ type: "agent", content });
                    } catch (e) {}
                    break;

                case 'error':
                    this.isLoading = false;
                    this.agentBusy = false;
                    this.addMessage({
                        type: "agent",
                        content: data.data.message,
                    });
                    break;

                case 'agent_received_feedback':
                    this.addMessage({
                        type: "system",
                        content: data.data && data.data.feedback ? data.data.feedback : "Agent geribildirim aldı.",
                    });
                    break;

                case 'ui_update_required':
                    // UI güncellemesi gerekli
                    this.emitUIUpdates([{ success: true }]);
                    this.addMessage({
                        type: "system",
                        content: "🔄 " + (data.data.message || "Diagram güncellendi"),
                    });
                    break;

                case 'conversation_cycle_started':
                    this.addMessage({
                        type: "system",
                        content: `🔍 ${data.data.message} (Max ${data.data.maxCycles} adım)`,
                    });
                    break;

                case 'cycle_step':
                    // Sadece her 5. adımda göster (spam önleme)
                    if (data.data.step % 5 === 1 || data.data.step <= 3) {
                        this.addMessage({
                            type: "system", 
                            content: `⚙️ ${data.data.message}`,
                        });
                    }
                    break;

                case 'ai_request_started':
                    this.addMessage({
                        type: "system",
                        content: `🤖 ${data.data.message}`,
                    });
                    break;

                case 'ai_response_received':
                    this.addMessage({
                        type: "system",
                        content: `✅ ${data.data.message}`,
                    });
                    break;

                case 'functions_processed':
                    this.addMessage({
                        type: "system", 
                        content: `🔧 ${data.data.message}`,
                    });
                    break;

                case 'conversation_cycle_completed':
                    this.addMessage({
                        type: "system",
                        content: `🎉 ${data.data.message} (${data.data.totalSteps} adımda)`,
                    });
                    break;

                case 'cycle_warning':
                    this.addMessage({
                        type: "system",
                        content: `⚠️ ${data.data.message}`,
                    });
                    break;

                case 'cycle_limit_reached':
                    this.addMessage({
                        type: "system",
                        content: `🚫 ${data.data.message} (${data.data.totalSteps}/${data.data.maxSteps})`,
                    });
                    break;

                case 'ai_error':
                    this.addMessage({
                        type: "system",
                        content: `❌ ${data.data.message}`,
                    });
                    break;

                // Bağlantı kuruldu mesajı (ilk mesaj)
                case undefined:
                    // İlk {"message":"Bağlantı kuruldu"} mesajını handle et
                    if (data.message === 'Bağlantı kuruldu') {
                        this.connectionStatus = 'connected';
                        this.addMessage({
                            type: "system",
                            content: "🔗 Supsis Asistan bağlantısı kuruldu",
                        });
                    }
                    break;

                default:
                    console.log('Bilinmeyen SSE mesaj tipi:', data.type, data);
                    
                    // Eğer data.message varsa ve type yoksa, genel mesaj olarak göster
                    if (data.message && !data.type) {
                        this.addMessage({
                            type: "system",
                            content: data.message,
                        });
                    }
            }
        },

        /**
         * Function status'unu güncelle
         */
        updateFunctionStatus(functionId, status, summary = null, error = null) {
            // Son function mesajını bul
            for (let i = this.messages.length - 1; i >= 0; i--) {
                const message = this.messages[i];
                if (message.type === "function") {
                    const func = message.functions.find(f => f.id === functionId);
                    if (func) {
                        func.status = status;
                        if (summary) func.summary = summary;
                        if (error) func.error = error;
                        break;
                    }
                }
            }
        },



        addMessage(messageData) {
            const message = {
                id: this.generateUUID(),
                timestamp: new Date(),
                ...messageData,
            };

            this.messages.push(message);

            // Auto scroll
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },

        generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },

        emitUIUpdates(results) {
            // Başarılı işlemler için UI güncellemelerini parent'a bildir
            const hasSuccessfulChanges = results.some(result => result.success);
            
            if (hasSuccessfulChanges) {
                // Diagram güncellenmiş olabilir, parent'a bildir
                this.$emit("diagram-updated");
                
                // Story'yi yeniden yükle (Vuex store güncelle)
                this.refreshStory();
            }
        },

        async refreshStory() {
            try {
                // Vuex store'dan story'yi yeniden yükle
                await this.$store.dispatch("CB/updateStory", {
                    id: this.storyId,
                    version: this.version
                });
                
                console.log("Story başarıyla yenilendi");
            } catch (error) {
                console.error("Story yenilenemedi:", error);
            }
        },

        scrollToBottom() {
            const container = this.$refs.messagesContainer;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        },

        formatTime(timestamp) {
            return new Date(timestamp).toLocaleTimeString("tr-TR", {
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        formatMessage(content) {
            // Basit markdown formatlaması
            return content
                .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                .replace(/\*(.*?)\*/g, "<em>$1</em>")
                .replace(/`(.*?)`/g, "<code>$1</code>")
                .replace(/\n/g, "<br>");
        },

        getFunctionDisplayName(functionName) {
            const displayNames = {
                get_story_detail: "Story Analizi",
                add_block: "Blok Ekleme",
                update_block: "Blok Güncelleme", 
                delete_block: "Blok Silme",
                move_block: "Blok Taşıma",
                copy_block: "Blok Kopyalama",
                update_story_name: "Story Adı Güncelleme",
                undo_diagram: "Geri Alma",
                redo_diagram: "İleri Alma",
                get_story_lite: "Hızlı Story Okuma",
                get_flattened_diagram: "Blok Listesi Analizi",
                get_story_tree_lite: "Tree Yapısı (Lite)",
                get_story_tree_full: "Tree Yapısı (Full)",
                get_block_names_tree: "Blok İsimleri",
                get_single_block: "Tek Blok Okuma",
                get_block_with_lite_children: "Blok + Lite Children",
                get_subtree: "Alt Ağaç Okuma",
            };
            return displayNames[functionName] || functionName;
        },

        handleEnterKey(event) {
            if (event.shiftKey) {
                // Shift+Enter: Yeni satır
                return;
            }
            
            event.preventDefault();
            this.sendMessage();
        },

        handleKeyDown(event) {
            if (event.key === "Escape") {
                this.handleClose();
            }
        },

        handleClose() {
            this.$emit("close-menu", 0);
        },
    },
};
</script>

<style lang="scss" scoped>
.chatbot-agent {
    border-radius: 15px;
    box-shadow: 0 0 15px rgb(0 0 0 / 10%);
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    height: calc(100vh - 150px);
    max-height: 100%;
    max-width: 100%;
    overflow: hidden;
    pointer-events: all;
    position: relative;
    user-select: text;
    width: 480px;
    z-index: 999;
    background: white;

    .header {
        align-items: center;
        border-radius: 15px 15px 0 0;
        display: flex;
        height: 80px;
        padding: 0 24px;
        justify-content: space-between;
        background: white;
        border-bottom: 1px solid #f0f0f0;

        .md-button {
            min-width: auto;
        }
    }

    .chat-conversation {
        flex-grow: 1;
        height: 0;
        position: relative;
        background: linear-gradient(180deg, #fafbfc, #f0f2f5);

        .conversation {
            display: flex;
            flex: 1 0 0;
            flex-direction: column;
            height: 100%;
            overflow-x: hidden;
            overflow-y: auto;
            overscroll-behavior-y: contain;
            width: 100%;
        }
    }

    .footer {
        background: white;
        align-items: center;
        border-radius: 0 0 15px 15px;
        display: flex;
        width: 100%;
        min-height: 70px;
    }
}

.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(24, 144, 255, 0.2);
        border-radius: 3px;
        
        &:hover {
            background: rgba(24, 144, 255, 0.4);
        }
    }
}

.welcome-message {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;

    .welcome-content {
        text-align: center;
        max-width: 500px;

        img {
            margin-bottom: 16px;
        }

        h4 {
            margin-bottom: 8px;
            color: #262626;
            font-size: 20px;
        }

        p {
            color: #8c8c8c;
            margin-bottom: 24px;
            font-size: 14px;
        }

        .quick-actions {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
            gap: 12px;

            .quick-action {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 16px;
                border: 1px solid #e8e8e8;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;

                &:hover {
                    border-color: #1890ff;
                    background: #f6f9ff;
                }

                .material-icons {
                    font-size: 24px;
                    color: #1890ff;
                    margin-bottom: 8px;
                }

                span {
                    font-size: 12px;
                    color: #595959;
                    text-align: center;
                }
            }
        }
    }
}

.message {
    &.user {
        display: flex;
        justify-content: flex-end;
    }

    &.agent, &.function {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
    }

    &.system {
        display: flex;
        justify-content: center;
    }
}

.user-message {
    .message-content {
        background: linear-gradient(135deg, #1890ff, #096dd9);
        color: white;
        padding: 14px 18px;
        border-radius: 20px 20px 4px 20px;
        max-width: 85%;
        word-wrap: break-word;
        line-height: 1.5;
        box-shadow: 0 2px 12px rgba(24, 144, 255, 0.15);
        font-size: 14px;
    }

    .message-time {
        font-size: 11px;
        color: #8c8c8c;
        margin-top: 6px;
        text-align: right;
    }
}

.system-message {
    display: flex;
    justify-content: center;
    margin: 4px 0;

    .system-content {
        display: flex;
        align-items: center;
        gap: 6px;
        background: rgba(24, 144, 255, 0.08);
        border: 1px solid rgba(24, 144, 255, 0.15);
        padding: 4px 12px;
        border-radius: 16px;
        font-size: 12px;
        color: #1890ff;
        box-shadow: 0 1px 4px rgba(24, 144, 255, 0.06);
        max-width: 90%;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.3;

        .material-icons {
            font-size: 14px;
            color: #1890ff;
            flex-shrink: 0;
        }
    }

    .message-time {
        display: none; // Sistem mesajlarında zaman gösterme
    }
}

.agent-message {
    .agent-avatar {
        flex-shrink: 0;
        margin-top: 0;
        margin-right: -6px;
        position: relative;
        z-index: 2;

        img {
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 3px 15px rgba(24, 144, 255, 0.2);
            width: 36px;
            height: 36px;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            right: 0;
            width: 12px;
            height: 12px;
            background: #52c41a;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
    }

    .message-bubble {
        background: white;
        padding: 14px 20px;
        border-radius: 24px 24px 24px 8px;
        max-width: 92%;
        border: 1px solid #e8f4fd;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        position: relative;
        margin-left: 6px;
        
        .message-content {
            word-wrap: break-word;
            line-height: 1.5;
            color: #262626;
            font-size: 14px;

            :deep(strong) {
                font-weight: 600;
                color: #1890ff;
            }

            :deep(em) {
                font-style: italic;
                color: #595959;
            }

            :deep(code) {
                background: #f0f8ff;
                padding: 3px 8px;
                border-radius: 6px;
                font-family: 'Monaco', 'Consolas', monospace;
                font-size: 13px;
                color: #1890ff;
                border: 1px solid #e8f4fd;
            }

            :deep(pre) {
                background: #f0f8ff;
                padding: 12px;
                border-radius: 8px;
                border: 1px solid #e8f4fd;
                overflow-x: auto;
                margin: 8px 0;
                
                code {
                    background: none;
                    border: none;
                    padding: 0;
                }
            }
        }

        .message-time {
            font-size: 11px;
            color: #8c8c8c;
            margin-top: 6px;
        }

        &::before {
            content: '';
            position: absolute;
            left: -12px;
            bottom: 12px;
            width: 0;
            height: 0;
            border: 12px solid transparent;
            border-right-color: white;
            border-left: none;
            filter: drop-shadow(-1px 1px 2px rgba(0, 0, 0, 0.05));
        }
    }

    .message-bubble.modern {
        background: white;
        border: 1px solid #e8f4fd;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    }
}

.function-message {
    .agent-avatar {
        flex-shrink: 0;
        margin-top: 0;
        margin-right: -6px;
        position: relative;
        z-index: 2;

        img {
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 3px 15px rgba(24, 144, 255, 0.2);
            width: 36px;
            height: 36px;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            right: 0;
            width: 12px;
            height: 12px;
            background: #faad14;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
    }

    .function-bubble {
        background: linear-gradient(135deg, #f0f8ff, #e6f7ff);
        border: 1px solid #b7eb8f;
        padding: 14px 20px;
        border-radius: 24px 24px 24px 8px;
        max-width: 92%;
        box-shadow: 0 4px 20px rgba(24, 144, 255, 0.06);
        position: relative;
        margin-left: 6px;

        &::before {
            content: '';
            position: absolute;
            left: -12px;
            bottom: 12px;
            width: 0;
            height: 0;
            border: 12px solid transparent;
            border-right-color: #f0f8ff;
            border-left: none;
            filter: drop-shadow(-1px 1px 2px rgba(0, 0, 0, 0.05));
        }

        .function-header {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 12px;
            font-weight: 600;
            color: #1890ff;
            font-size: 14px;

            .material-icons {
                font-size: 18px;
                color: #faad14;
            }
        }

        .function-list {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .function-item {
                display: flex;
                align-items: flex-start;
                gap: 10px;
                padding: 8px 12px;
                background: rgba(255, 255, 255, 0.8);
                border-radius: 12px;
                border: 1px solid rgba(24, 144, 255, 0.1);
                font-size: 13px;
                transition: all 0.2s ease;

                .function-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;

                    .function-name {
                        font-weight: 500;
                        font-size: 13px;
                        line-height: 1.3;
                    }

                    .function-summary {
                        font-size: 12px;
                        color: #666;
                        font-style: italic;
                        line-height: 1.3;
                    }

                    .function-error {
                        font-size: 12px;
                        color: #f5222d;
                        font-style: italic;
                        line-height: 1.3;
                    }
                }

                &.pending {
                    background: rgba(24, 144, 255, 0.05);
                    border-color: rgba(24, 144, 255, 0.2);
                    
                    .function-name {
                        color: #1890ff;
                    }
                }

                &.executing {
                    background: rgba(250, 173, 20, 0.05);
                    border-color: rgba(250, 173, 20, 0.2);
                    
                    .function-name {
                        color: #faad14;
                    }
                }

                &.completed {
                    background: rgba(82, 196, 26, 0.05);
                    border-color: rgba(82, 196, 26, 0.2);
                    
                    .function-name {
                        color: #52c41a;
                    }
                }

                &.failed {
                    background: rgba(245, 34, 45, 0.05);
                    border-color: rgba(245, 34, 45, 0.2);
                    
                    .function-name {
                        color: #f5222d;
                    }
                }
            }
        }
    }
}

.loading-message {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;

    .agent-avatar {
        flex-shrink: 0;
        margin-top: 0;
        margin-right: -6px;
        position: relative;
        z-index: 2;

        img {
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 3px 15px rgba(24, 144, 255, 0.2);
            width: 36px;
            height: 36px;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            right: 0;
            width: 12px;
            height: 12px;
            background: #1890ff;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            animation: pulse 1.5s infinite;
        }
    }

    .loading-bubble {
        background: white;
        padding: 14px 20px;
        border-radius: 24px 24px 24px 8px;
        border: 1px solid #e8f4fd;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        position: relative;
        margin-left: 6px;

        &::before {
            content: '';
            position: absolute;
            left: -12px;
            bottom: 12px;
            width: 0;
            height: 0;
            border: 12px solid transparent;
            border-right-color: white;
            border-left: none;
            filter: drop-shadow(-1px 1px 2px rgba(0, 0, 0, 0.05));
        }

        .typing-indicator {
            display: flex;
            gap: 6px;

            span {
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background: #1890ff;
                animation: typing 1.4s infinite ease-in-out;

                &:nth-child(1) {
                    animation-delay: -0.32s;
                }

                &:nth-child(2) {
                    animation-delay: -0.16s;
                }
            }
        }
    }
}

@keyframes typing {
    0%, 80%, 100% {
        transform: scale(0.8);
        opacity: 0.5;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.1);
        opacity: 0.7;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.chat-input {
    padding: 20px 24px;
    width: 100%;
    background: linear-gradient(180deg, #fafbfc, white);
    border-top: 1px solid #e8f4fd;

    .input-container {
        display: flex;
        gap: 8px;
        align-items: flex-end;
        background: white;
        border-radius: 24px;
        padding: 8px 8px 8px 20px;
        border: 1px solid #e8e8e8;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        transition: all 0.25s ease;

        &:focus-within {
            border-color: #1890ff;
            box-shadow: 0 4px 16px rgba(24, 144, 255, 0.12);
        }

        :deep(.ant-input) {
            border: none !important;
            border-radius: 0;
            padding: 8px 0;
            flex: 1;
            background: transparent;
            font-size: 14px;
            line-height: 1.4;
            resize: none;
            min-height: 20px;
            box-shadow: none !important;

            &:focus, &:hover, &:active {
                border: none !important;
                box-shadow: none !important;
                outline: none !important;
            }

            &::placeholder {
                color: #a6a6a6;
                font-style: normal;
                font-weight: 400;
            }
        }

        .send-button {
            border-radius: 50%;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            background: linear-gradient(135deg, #1890ff, #096dd9);
            border: none;
            box-shadow: 0 3px 12px rgba(24, 144, 255, 0.3);
            transition: all 0.2s ease;
            position: relative;

            &::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(45deg);
                width: 0;
                height: 0;
                border-left: 6px solid transparent;
                border-right: 6px solid transparent;
                border-bottom: 8px solid white;
                margin-left: 1px;
            }

            &:hover:not(:disabled) {
                background: linear-gradient(135deg, #096dd9, #0050b3);
                box-shadow: 0 4px 16px rgba(24, 144, 255, 0.4);
                transform: translateY(-1px);
            }

            &:active:not(:disabled) {
                transform: translateY(0) scale(0.95);
            }

            &:disabled {
                background: #f0f0f0;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
                cursor: not-allowed;
                transform: none;
                
                &::before {
                    border-bottom-color: #bfbfbf;
                }
            }

            .anticon {
                display: none;
            }
        }
    }
}
</style>
